import { Link } from "@inertiajs/react";
import { cn } from "@/lib/utils";

export interface Article {
  title: string;
  excerpt: string;
  slug: string;
  published_at: string;
  tags: string[];
  featured_image?: string;
}

interface Props {
  articles: Article[];
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
}

function formatDate(date: string) {
  const d = new Date(date);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

export function LatestArticles({
  articles,
  title = <>Latest <span className="text-primary">Infralyst</span> articles</>,
  subtitle = "Deep dives on AWS cost optimization, Terraform, and infrastructure best practices.",
}: Props): React.ReactElement {
  if (articles.length === 0) {
    return <></>;
  }

  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          {title}
        </h2>
        {subtitle && (
          <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
            {subtitle}
          </p>
        )}
        <div className={cn("grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto", !subtitle && "mt-12")}>
          {articles.map((article) => (
            <Link
              key={article.slug}
              href={`/articles/${article.slug}`}
              className="group flex flex-col rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors overflow-hidden"
            >
              {article.featured_image && (
                <div className="aspect-[16/9] overflow-hidden bg-slate-100 dark:bg-slate-800">
                  <img
                    src={article.featured_image}
                    alt={article.title}
                    className="object-cover w-full h-full"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="flex-1 flex flex-col p-6">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block rounded-full bg-[#E6A13C] text-white px-3 py-1 text-xs font-semibold"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h3 className="text-xl font-bold mb-2 group-hover:underline transition">
                  {article.title}
                </h3>
                <p className="text-surface-foreground/80 text-sm leading-relaxed mb-4 line-clamp-2">
                  {article.excerpt}
                </p>
                <span className="mt-auto text-xs text-surface-foreground/60">
                  {formatDate(article.published_at)}
                </span>
              </div>
            </Link>
          ))}
        </div>
        <p className="text-center text-surface-foreground/60 text-sm mt-12">
          Want more?{" "}
          <a href="/articles" className="text-primary hover:underline">
            View all articles
          </a>
        </p>
      </div>
    </section>
  );
}
